/* eslint-disable max-lines-per-function */
import {
  CursorPosition,
  Point,
} from '@/domain/dienstplan/features/VisualEditor/types';
import {
  Belegung,
  BelegungsIntervall,
} from '@/domain/dienstplan/shared/types/Belegung';
import type { CSSProperties } from 'react';
import { createStore } from 'zustand';

interface VisualEditorProps {
  data: {
    belegungen: BelegungsIntervall[];
  };
  ui: {
    cursorPosition: CursorPosition | undefined;
    popoverPosition: CSSProperties | undefined;
    isPopoverOpen: boolean;
    hoveredBelegung: Belegung | undefined;
    startBelegungPosition: Point | undefined;
    isResizingStart: boolean;
    isResizingEnde: boolean;
    isCreatingNewBelegung: boolean;
    xAxisOffset: number;
  };
}

export interface VisualEditorState extends VisualEditorProps {
  addBelegung: (belegung: BelegungsIntervall) => void;
  removeBelegungById: (id: string) => void;
  setStartBelegungPosition: (point: Point) => void;
  updateBelegungById: (updatedBelegung: BelegungsIntervall, id: string) => void;
  setIsResizingStart: (value: boolean) => void;
  setIsResizingEnde: (value: boolean) => void;
  setIsCreatingNewBelegung: (value: boolean) => void;
  setCursorPosition: (position: CursorPosition) => void;
  setPopoverPosition: (styles: CSSProperties) => void;
  setHoveredBelegung: (belegung: Belegung) => void;
  setIsPopoverOpen: (isOpen: boolean) => void;
}

export type VisualEditorStore = ReturnType<typeof createVisualEditorStore>;

export const createVisualEditorStore = (
  belegungen: BelegungsIntervall[],
  xAxisOffset: number,
) => {
  const defaultProps: VisualEditorProps = {
    data: {
      belegungen: [],
    },
    ui: {
      cursorPosition: { x: 0, y: 0 },
      popoverPosition: undefined,
      startBelegungPosition: undefined,
      isResizingStart: false,
      isResizingEnde: false,
      isCreatingNewBelegung: false,
      hoveredBelegung: undefined,
      isPopoverOpen: false,
      xAxisOffset: 0,
    },
  };

  return createStore<VisualEditorState>()((set) => ({
    data: {
      belegungen,
    },
    ui: {
      ...defaultProps.ui,
      xAxisOffset,
    },
    addBelegung: (belegung) =>
      set((state) => ({
        data: { belegungen: [...state.data.belegungen, belegung] },
      })),
    setStartBelegungPosition: (point) =>
      set((state) => ({
        ui: { ...state.ui, startBelegungPosition: point },
      })),
    updateBelegungById: (updatedBelegung, id) =>
      set((state) => {
        const inactiveBelegungen = state.data.belegungen.filter(
          (b) => b.belegung.id !== id,
        );
        return {
          data: { belegungen: [...inactiveBelegungen, updatedBelegung] },
        };
      }),
    removeBelegungById: (id) =>
      set((state) => {
        return {
          data: {
            belegungen: state.data.belegungen.filter(
              (b) => b.belegung.id !== id,
            ),
          },
        };
      }),
    setIsResizingStart: (value) =>
      set((state) => ({
        ui: { ...state.ui, isResizingStart: value },
      })),
    setIsResizingEnde: (value) =>
      set((state) => ({
        ui: { ...state.ui, isResizingEnde: value },
      })),
    setIsCreatingNewBelegung: (value) =>
      set((state) => ({
        ui: { ...state.ui, isCreatingNewBelegung: value },
      })),
    setCursorPosition: (value) =>
      set((state) => ({
        ui: { ...state.ui, cursorPosition: value },
      })),
    setPopoverPosition: (value) =>
      set((state) => ({
        ui: { ...state.ui, popoverPosition: value },
      })),
    setHoveredBelegung: (value) =>
      set((state) => ({
        ui: { ...state.ui, hoveredBelegung: value },
      })),
    setIsPopoverOpen: (value) =>
      set((state) => ({
        ui: { ...state.ui, isPopoverOpen: value },
      })),
  }));
};
